import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';

import { ActionIcon, Center, Group, Loader, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconInfoSquareRoundedFilled } from '@tabler/icons-react';
import { dateToDateString } from '@utils/date';

import { InvoicesSortBy } from '@api/invoices.api';
import {
  GetOffersQueryParams,
  useGetOffersCountQuery,
  useGetOffersQuery,
} from '@api/offers.api';

import { LayoutContextType } from '@components/layout/Layout';
import SearchBar from '@components/SearchBar';
import { Offer } from '@interfaces/offers.interface';

export default function Offers() {
  const [searchParams] = useSearchParams();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetOffersQueryParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'id',
    sortOrder: 'desc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Offer>>({
    columnAccessor: 'id',
    direction: 'desc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as InvoicesSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================

  // Get initial orders count
  const { data: offersCount = { count: 0 }, isLoading: isLoadingOffersCount } =
    useGetOffersCountQuery(searchQuery);

  const { data = [], isLoading } = useGetOffersQuery({
    ...filters,
    searchQuery,
  });

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Offerte',
    });
  }, [setLayoutProps]);

  return isLoading || isLoadingOffersCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : (
    <>
      <Group mb="lg">
        <SearchBar
          placeholder="Cerca numero offerta"
          value={filters.searchQuery}
          onChange={(newValue) =>
            setFilters({ ...filters, searchQuery: newValue })
          }
        />
      </Group>
      <DataTable
        striped
        withTableBorder
        noRecordsText="Nessun offerta trovata"
        minHeight={data.length === 0 ? 200 : undefined}
        columns={[
          {
            accessor: 'date',
            title: 'Data',
            sortable: true,
            render: (record) => dateToDateString(new Date(record.date)),
          },
          {
            accessor: 'id',
            title: 'Numero offerta',
            sortable: true,
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Tooltip label="Dettagli offerta">
                <ActionIcon
                  variant="subtle"
                  component={Link}
                  to={`/offerte/${record.id}`}
                >
                  <IconInfoSquareRoundedFilled />
                </ActionIcon>
              </Tooltip>
            ),
          },
        ]}
        records={data}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={offersCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
